/* assets/product-print.js
 * Print Product (NO PRICE) - PRO (Final)
 *
 * ✅ If ".icon-btn.icon-btn--doc" exists -> print its PDF
 * ✅ Else -> print a professional product sheet:
 *    - logo + title
 *    - image
 *    - description (DOM preferred, product.js fallback)
 *    - selected variant (SKU + option rows)
 *    - ALL variants list (title + sku)
 *    - ALL metafields rows from DOM
 *
 * Includes anti-double-open protections + popup-block fallback (iframe print)
 */
(() => {
  if (window.__YG_PRODUCT_PRINT_LOADED__ === true) return;
  window.__YG_PRODUCT_PRINT_LOADED__ = true;

  // -------------------------
  // Lock (anti double click)
  // -------------------------
  const LOCK_KEY = "__YG_PRODUCT_PRINT_LOCK__";
  const lock = () => {
    if (window[LOCK_KEY]) return false;
    window[LOCK_KEY] = true;
    setTimeout(() => (window[LOCK_KEY] = false), 1200);
    return true;
  };

  // -------------------------
  // Helpers
  // -------------------------
  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const norm = (v) => {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
    if (typeof v === "object") {
      if ("name" in v) return String(v.name ?? "");
      if ("title" in v) return String(v.title ?? "");
      if ("label" in v) return String(v.label ?? "");
      if ("value" in v) return String(v.value ?? "");
      try {
        return JSON.stringify(v);
      } catch {
        return "";
      }
    }
    return String(v);
  };

  const toAbsUrl = (u) => {
    try {
      return new URL(u, location.origin).toString();
    } catch {
      return u || "";
    }
  };

  const getHandle = () => {
    const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") || "";
    const m1 = canonical.match(/\/products\/([^/?#]+)/);
    if (m1?.[1]) return m1[1];

    const og = document.querySelector('meta[property="og:url"]')?.getAttribute("content") || "";
    const m2 = og.match(/\/products\/([^/?#]+)/);
    if (m2?.[1]) return m2[1];

    const m3 = location.pathname.match(/\/products\/([^/?#]+)/);
    return m3?.[1] || "";
  };

  const getSelectedVariantId = () => {
    const input =
      document.querySelector('form[action^="/cart/add"] [name="id"]') ||
      document.querySelector('input[name="id"]') ||
      document.querySelector('select[name="id"]');
    const v = input?.value;
    return v ? Number(v) : null;
  };

  // -------------------------
  // ✅ PDF detection by DOC button (your rule)
  // -------------------------
  const findPdfFromDocButton = () => {
    const docEl = document.querySelector(".icon-btn.icon-btn--doc");
    if (!docEl) return "";

    // If it's an <a>
    if (docEl.tagName === "A") {
      const href = docEl.getAttribute("href") || "";
      if (href) return toAbsUrl(href);
    }

    // If button inside <a>
    const a = docEl.closest("a[href]");
    if (a) {
      const href = a.getAttribute("href") || "";
      if (href) return toAbsUrl(href);
    }

    // Try data attributes
    const dataUrl =
      docEl.getAttribute("data-pdf") ||
      docEl.getAttribute("data-url") ||
      docEl.getAttribute("data-href") ||
      docEl.getAttribute("data-file") ||
      "";

    if (dataUrl) return toAbsUrl(dataUrl);

    return "";
  };

  // -------------------------
  // Print PDF
  // -------------------------
  const openWin = (url) => window.open(url, "_blank", "width=980,height=980");

  const printPdf = (pdfUrl) => {
    const url = toAbsUrl(pdfUrl);
    const w = openWin(url);
    const popupBlocked = !w;

    if (!popupBlocked) {
      let tries = 0;
      const tick = () => {
        tries++;
        try {
          w.focus();
          w.print();
        } catch {}
        if (tries < 7) setTimeout(tick, 550);
      };
      setTimeout(tick, 750);
      return;
    }

    // Popup blocked → iframe fallback
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    iframe.src = url;
    document.body.appendChild(iframe);

    const cleanup = () => setTimeout(() => iframe.remove(), 1800);

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {}
      cleanup();
    };

    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {}
      cleanup();
    }, 1600);
  };

  // -------------------------
  // ✅ Metafields from DOM (ALL)
  // -------------------------
  const extractLabelFromItem = (itemEl) => {
    if (!itemEl) return "";

    const labelEl =
      itemEl.querySelector(".product__metafield-label") ||
      itemEl.querySelector("[data-label]") ||
      itemEl.querySelector("[aria-label]");

    if (labelEl) {
      const dl = labelEl.getAttribute?.("data-label");
      if (dl && dl.trim()) return dl.trim();

      const al = labelEl.getAttribute?.("aria-label");
      if (al && al.trim()) return al.trim();

      const t = (labelEl.textContent || "").trim();
      if (t) return t;
    }

    const valueEl = itemEl.querySelector(".product__metafield-value");
    const kids = Array.from(itemEl.children || []);
    for (const k of kids) {
      if (valueEl && (k === valueEl || k.contains(valueEl))) continue;
      const txt = (k.textContent || "").trim();
      if (txt) return txt;
    }

    return "יצרן";
  };

  const collectMetafieldRowsFromDOM = () => {
    const groups = Array.from(document.querySelectorAll(".product__metafields-group"));
    const rows = [];

    for (const g of groups) {
      const items = Array.from(g.querySelectorAll(".product__metafield-item"));
      for (const item of items) {
        const valueEl = item.querySelector(".product__metafield-value");
        const val = (valueEl?.textContent || "").trim();
        if (!val) continue;

        const key = extractLabelFromItem(item);
        const cleanKey = (key || "").replace(/\s+/g, " ").trim();

        if (!rows.some((r) => r.key === cleanKey && r.val === val)) {
          rows.push({ key: cleanKey || "שדה", val });
        }
      }
    }
    return rows;
  };

  // -------------------------
  // ✅ Description from DOM (prefer product description area)
  // -------------------------
  const cleanHtmlForPrint = (html) => {
    const wrap = document.createElement("div");
    wrap.innerHTML = String(html || "");

    // remove scripts/styles
    wrap.querySelectorAll("script,style,noscript,template").forEach((n) => n.remove());

    // remove obvious policy areas
    wrap.querySelectorAll(".shopify-policy__container,[class*='policy'],[id*='policy']").forEach((n) => n.remove());

    return wrap.innerHTML.trim();
  };

  const getDescriptionFromDOM = () => {
    const selectors = [
      "[data-product-description]",
      ".product__description",
      ".product__description .rte",
      ".product__info-wrapper .rte",
      ".product__info-container .rte",
      "details[open] .rte",
      ".rte",
    ];

    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (!el) continue;

      const raw = (el.innerHTML || "").trim();
      if (!raw) continue;

      const cleaned = cleanHtmlForPrint(raw);
      const plain = cleaned.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
      if (plain.length < 25) continue;

      return cleaned;
    }

    return "";
  };

  // -------------------------
  // ✅ Variant options as rows (selected)
  // -------------------------
  const buildChosenOptions = (product, variant) => {
    const optionNamesRaw = Array.isArray(product?.options) ? product.options : [];
    const optionNames = optionNamesRaw
      .map((o) => (typeof o === "string" ? o : norm(o?.name || o?.title || o)))
      .filter(Boolean);

    let values = [];
    if (Array.isArray(variant?.options) && variant.options.length) {
      values = variant.options.map((v) => norm(v));
    } else {
      values = [variant?.option1, variant?.option2, variant?.option3].map((v) => norm(v)).filter(Boolean);
    }

    const pairs = [];
    for (let i = 0; i < Math.max(optionNames.length, values.length); i++) {
      const key = optionNames[i] || `אפשרות ${i + 1}`;
      const val = values[i] || "";
      if (val && val.toLowerCase() !== "default title") pairs.push({ key, val });
    }
    return pairs;
  };

  // -------------------------
  // ✅ All variants list (title + sku)
  // -------------------------
  const buildVariantsList = (product) => {
    const vars = Array.isArray(product?.variants) ? product.variants : [];
    const rows = vars
      .map((v) => {
        const title = (v?.public_title || v?.name || v?.title || "").trim();
        const sku = (v?.sku || "").trim();
        const available = v?.available === false ? " (לא זמין)" : "";
        const line = [title || "וריאנט", sku ? `— ${sku}` : ""].join(" ").trim();
        return line ? `${escapeHtml(line)}${available}` : "";
      })
      .filter(Boolean);

    if (!rows.length) return "";

    return `
      <div class="variantsList">
        ${rows.map((t) => `<div class="variantsList__row">${t}</div>`).join("")}
      </div>
    `.trim();
  };

  // -------------------------
  // Build spec table
  // -------------------------
  const buildSpecTable = (pairs) => {
    const safe = Array.isArray(pairs) ? pairs : [];
    const rows = safe
      .filter((p) => p && p.key && p.val)
      .map(
        (p) => `
          <tr>
            <th>${escapeHtml(p.key)}</th>
            <td>${escapeHtml(p.val)}</td>
          </tr>
        `.trim()
      )
      .join("");

    if (!rows) return "";
    return `
      <table class="spec" role="table" aria-label="Product spec">
        <tbody>${rows}</tbody>
      </table>
    `.trim();
  };

  // -------------------------
  // Build professional print page (includes description + variants + metafields)
  // -------------------------
  const buildPrintHtml = ({ logoUrl, product, variant, optionPairs, domPairs }) => {
    const title = escapeHtml(product?.title || "");
    const category = norm(product?.type || "").trim();
    const vendor = norm(product?.vendor || "").trim();
    const sku = norm(variant?.sku || product?.variants?.[0]?.sku || "").trim();

    const img =
      (typeof product?.featured_image === "string" && product.featured_image) ||
      (Array.isArray(product?.images) && product.images[0]) ||
      "";

    const image = img ? escapeHtml(img) : "";

    // description: DOM preferred, fallback to product.js
    const domDesc = getDescriptionFromDOM();
    const fallbackDesc = product?.description ? cleanHtmlForPrint(String(product.description)) : "";
    const descHtml = domDesc || fallbackDesc;

    // merge rows: top fields + selected variant options + metafields
    const merged = [];
    const pushUnique = (k, v) => {
      const key = norm(k).replace(/\s+/g, " ").trim();
      const val = norm(v).replace(/\s+/g, " ").trim();
      if (!key || !val) return;
      if (!merged.some((x) => x.key === key && x.val === val)) merged.push({ key, val });
    };

    if (vendor) pushUnique("מותג", vendor);
    if (category) pushUnique("קטגוריה", category);
    if (sku) pushUnique('מק״ט (וריאנט נבחר)', sku);

    (optionPairs || []).forEach((p) => pushUnique(p.key, p.val)); // selected variant options
    (domPairs || []).forEach((p) => pushUnique(p.key, p.val));    // all metafields

    const specTable = buildSpecTable(merged);
    const variantsList = buildVariantsList(product);

    const descPlain = String(descHtml || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    const descBlock =
      descPlain.length > 20
        ? `
        <section class="block">
          <h2 class="h2">תיאור</h2>
          <div class="desc">${descHtml}</div>
        </section>
      `
        : "";

    const variantsBlock =
      variantsList
        ? `
        <section class="block">
        </section>
      `
        : "";

    return `
<!doctype html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${title}</title>
  <style>
    :root{
      --ink:#0b1220;
      --muted:#475569;
      --line:#e5e7eb;
      --soft:#f8fafc;
      --paper:#ffffff;
    }
    *{box-sizing:border-box;}
    html,body{background:var(--paper);color:var(--ink);}
    body{
      margin:0;
      font-family: Arial, system-ui, -apple-system, "Segoe UI", sans-serif;
      font-size:13.5px;
      line-height:1.55;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page{
      max-width:980px;
      margin:0 auto;
      padding:20px 22px;
    }

    .header{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap:14px;
      padding:8px 0 14px;
      border-bottom:2px solid var(--line);
    }
    .title{
      margin:0;
      font-size:20px;
      font-weight:900;
      line-height:1.25;
      letter-spacing:-0.2px;
    }
    .brand{
      display:flex;
      align-items:center;
      justify-content:flex-end;
      min-width:140px;
    }
    .brand img{
      height:28px;
      width:auto;
      object-fit:contain;
    }

    /* main grid: image LEFT, text RTL */
    .main{
      margin-top:14px;
      display:flex;
      gap:18px;
      direction:ltr;
      align-items:flex-start;
    }
    .left{width:120px;flex:0 0 120px;}
    .right{flex:1;direction:rtl;}

    .photo{
      border:1px solid var(--line);
      border-radius:10px;
      background:#fff;
      padding:8px;
      min-height:120px;
      display:flex;
      align-items:center;
      justify-content:center;
    }
    .photo img{
      max-width:100%;
      height:auto;
      max-height:98px;
      object-fit:contain;
    }
    .photo .fallback{
      font-size:11px;
      color:var(--muted);
      font-weight:800;
    }

    .block{margin-top:14px;}
    .h2{
      margin:0 0 8px 0;
      font-size:13px;
      font-weight:900;
      color:var(--ink);
    }

    table.spec{
      width:100%;
      border-collapse:collapse;
      border:1px solid var(--line);
      border-radius:10px;
      overflow:hidden;
      background:#fff;
    }
    table.spec th, table.spec td{
      padding:9px 10px;
      border-bottom:1px solid #f1f5f9;
      vertical-align:top;
      text-align:right;
    }
    table.spec th{
      width:240px;
      color:var(--muted);
      font-weight:900;
      background:var(--soft);
      border-left:1px solid #f1f5f9;
      white-space:nowrap;
    }
    table.spec td{
      font-weight:800;
      color:var(--ink);
      word-break:break-word;
    }
    table.spec tr:last-child th,
    table.spec tr:last-child td{border-bottom:none;}

    .desc{
      border:1px solid var(--line);
      border-radius:10px;
      padding:10px 12px;
      background:#fff;
      color:var(--ink);
    }
    .desc img{max-width:100%;height:auto;}

    .variantsList{
      border:1px solid var(--line);
      border-radius:10px;
      background:#fff;
      overflow:hidden;
    }
    .variantsList__row{
      padding:8px 10px;
      border-bottom:1px solid #f1f5f9;
      font-weight:800;
      color:var(--ink);
    }
    .variantsList__row:last-child{border-bottom:none;}

    .footer{
      margin-top:16px;
      padding-top:10px;
      border-top:1px solid var(--line);
      color:var(--muted);
      font-size:11px;
      font-weight:700;
      display:flex;
      justify-content:flex-end;
    }

    @page{margin:14mm;}
    @media print{
      .page{padding:0;}
    }
    @media (max-width:860px){
      .main{gap:12px;}
      table.spec th{width:170px;}
      .left{width:96px;flex-basis:96px;}
      .photo{min-height:96px;}
      .photo img{max-height:84px;}
    }
  </style>
</head>
<body>
  <div class="page">
    <header class="header">
      <h1 class="title">${title}</h1>
      <div class="brand">
        ${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="לוגו">` : ""}
      </div>
    </header>

    <section class="main">
      <aside class="left">
        <div class="photo">
          ${image ? `<img src="${image}" alt="">` : `<div class="fallback">ללא תמונה</div>`}
        </div>
      </aside>

      <div class="right">
        ${
          specTable
            ? `
          <section class="block">
            <h2 class="h2">מפרט נבחר</h2>
            ${specTable}
          </section>
        `
            : ""
        }

        ${variantsBlock}
        ${descBlock}
      </div>
    </section>

    <div class="footer">© Yashir Group</div>
  </div>

  <script>
    if (!window.__YG_ALREADY_PRINTED__) {
      window.__YG_ALREADY_PRINTED__ = true;
      setTimeout(() => { window.print(); }, 280);
    }
  </script>
</body>
</html>
    `.trim();
  };

  // -------------------------
  // Print via iframe fallback (when popup blocked)
  // -------------------------
  const printViaIframe = (html) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {}
      setTimeout(() => iframe.remove(), 1200);
    }, 420);
  };

  // -------------------------
  // Run
  // -------------------------
  const run = async (btn) => {
    if (!lock()) return;

    // 1) ✅ PDF exists if doc button exists
    const pdfUrl = findPdfFromDocButton();
    if (pdfUrl) {
      printPdf(pdfUrl);
      return;
    }

    // 2) Otherwise print full product sheet (description + variants + metafields)
    const domPairs = collectMetafieldRowsFromDOM();
    const logoUrl = btn.getAttribute("data-print-logo") || "";

    const handle = getHandle();
    if (!handle) return;

    let product = null;
    try {
      product = await fetch(`/products/${handle}.js`, { credentials: "same-origin" }).then((r) => r.json());
    } catch {}

    if (!product) return;

    const selectedVariantId = getSelectedVariantId();
    const variant =
      (selectedVariantId && product.variants?.find((v) => Number(v.id) === Number(selectedVariantId))) ||
      product.variants?.find((v) => v.available) ||
      product.variants?.[0] ||
      null;

    const optionPairs = variant ? buildChosenOptions(product, variant) : [];
    const html = buildPrintHtml({ logoUrl, product, variant, optionPairs, domPairs });

    // Try popup; fallback iframe
    const w = window.open("about:blank", "_blank", "width=980,height=980");
    const popupBlocked = !w;

    if (popupBlocked) {
      printViaIframe(html);
      return;
    }

    try {
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
    } catch {
      try { w.close(); } catch {}
      printViaIframe(html);
    }
  };

  // -------------------------
  // Bind print trigger buttons [data-product-print]
  // -------------------------
  const bind = () => {
    document.querySelectorAll("[data-product-print]").forEach((btn) => {
      if (btn.dataset.printBound === "1") return;
      btn.dataset.printBound = "1";

      if (btn.tagName === "BUTTON" && !btn.getAttribute("type")) btn.setAttribute("type", "button");

      btn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (e.detail && e.detail > 1) return;
          run(btn);
        },
        true
      );
    });
  };

  document.addEventListener("DOMContentLoaded", bind);
  document.addEventListener("shopify:section:load", bind);
})();
